// StudySmith App Configuration

export const CONFIG = {
  // Feature Flags
  features: {
    showLoginSignup: true, // Set to true to show login/signup pages
    showBetaBadge: true,
    showWaitingList: true,
    enableAnalytics: false, // Enable when ready for production
    debugMode: process.env.NODE_ENV === 'development',
    useUniversityLogos: true, // Set to true to use logos instead of text
    enableUniversityCarousel: false, // Enable carousel for university display
    showHeroStats: false, // Set to false to hide hero section statistics
    enableAuthAccess: true,
    enableAllLanguages: false, // Set to false to limit language options to Irish focus
    guideEmailNotifications: process.env.REACT_APP_GUIDE_EMAIL_NOTIFICATIONS !== 'disabled',
    // Debug specific features
    debugLinks: true, // Comprehensive link debugging
    debugTiptapEditor: false, // TiptapEditor specific debugging  
    debugMarkdownConversion: false, // Markdown to HTML conversion debugging
    debugTiptapTracking: false // Set to true to enable comprehensive tracking system for debugging
  },
  
  // Authentication Configuration
  auth: {
    accessCookieName: 'studysmith_auth_access',
    accessCookieValue: 'enabled',
    cookieExpireDays: 30,
    sessionCookieName: 'studysmith_session',
    enableOAuth: true,
    providers: {
      google: true,
      azure: true
    },
    // Supabase Configuration
    supabaseUrl: 'https://cisfmmphawxauuyqrrhc.supabase.co',
    // API Configuration
    apiBaseUrl: process.env.NODE_ENV === 'development'
      ? 'http://localhost:5001/api/v1'
      : 'https://studysmith-api.fly.dev/api/v1',
    endpoints: {
      signup: '/auth/signup',
      signin: '/auth/signin',
      signout: '/auth/signout',
      user: '/auth/user',
      refresh: '/auth/refresh',
      resetPassword: '/auth/reset-password',
      updateProfile: '/auth/update-profile',
      oauth: '/auth/oauth',
      health: '/auth/health'
    }
  },

  // Waiting List Configuration
  waitingList: {
    apiBaseUrl: process.env.NODE_ENV === 'development' 
      ? 'http://localhost:5001/api/v1' 
      : 'https://studysmith-api.fly.dev/api/v1',
    endpoint: '/waitlist'
  },

  // App Information
  app: {
    name: 'StudySmith',
    version: '1.0.0',
    description: 'AI-powered study companion that transforms your PDFs into personalized learning experiences.',
    supportEmail: 'support@studysmith.app',
    privacyUrl: '/privacy',
    termsUrl: '/terms',
    contactEmail: 'contact@studysmith.app'
  },

  // UI Configuration
  ui: {
    animationDuration: 300,
    maxFileSize: 100 * 1024 * 1024, // 100MB
    supportedFileTypes: ['.pdf', '.doc', '.docx', '.ppt', '.pptx', '.xls', '.xlsx', '.txt', '.md', '.html', '.css', '.js', '.json', '.csv', '.rtf'],
    theme: {
      primaryColor: '#2d7a2d',
      secondaryColor: '#1a5a1a'
    }
  },
  
  // Hero Section Stats
  stats: {
    show: true, // Master toggle for showing stats
    hoursPerWeek: '10+',
    successRate: '95%',
    studentsWaiting: '2,500+',
    hoursPerWeekSaved: '10+ Hours',
    studentsWorldwide: '257K+',
    marketSize: '€2B+',
    pricingValue: '€8.99'
  },
  
  // Pricing based on StudySmith Business Plan
  billing: {
    stripePublicKey: process.env.REACT_APP_STRIPE_PUBLIC_KEY || 'pk_live_51SCdf0LfvHgkIQoKnVtrk7lbO1OoIRUG6F4hWJDStP2xiAnRl47f3KylpY3wrwIYdQCUvy3DH2WSff8TnK6T1cKv00kbQx3Bar',
    successPath: '/dashboard/billing/success',
    cancelPath: '/dashboard'
  },

  pricing: {
    individual: {
      monthly: {
        price: 8.99,
        currency: '€',
        period: '/month',
        annualPrice: 107.88,
        annualPeriod: '/year'
      },
      annual: {
        price: 6.74, // 25% off monthly price (8.99 * 0.75)
        currency: '€',
        period: '/month',
        annualPrice: 80.88, // 6.74 * 12
        annualPeriod: '/year',
        savings: '25%'
      },
      name: 'StudySmith Plus',
      description: 'Everything you need to ace your exams',
      features: [
        'Automatic material collection from Canvas/Moodle',
        'AI-powered course analysis and breakdown',
        'Personalized study plans and schedules',
        'Interactive study guides with Q&A',
        'Practice questions from your materials',
        'Progress tracking and weak spot identification',
        'All-in-one study dashboard',
        'Email support'
      ]
    },
    ultimate: {
      monthly: {
        price: 14.99,
        currency: '€',
        period: '/month',
        annualPrice: 179.88,
        annualPeriod: '/year'
      },
      annual: {
        price: 11.24, // 25% off monthly price (14.99 * 0.75)
        currency: '€',
        period: '/month',
        annualPrice: 134.88, // 11.24 * 12
        annualPeriod: '/year',
        savings: '25%'
      },
      name: 'StudySmith Ultimate',
      description: 'Maximum AI power for the most demanding students',
      features: [
        'Everything in Plus',
        '3x AI answer generation limits',
        'Advanced study guide generation',
        'Extended AI tutor chat availability',
        'Priority processing for faster results',
        'Advanced learning analytics',
        'Dynamic content updates throughout semester',
        'Early access to new features',
        'Priority email and chat support'
      ]
    },
    institutional: {
      name: 'StudySmith University',
      description: 'Enterprise solution for educational institutions',
      price: 'Contact Sales',
      minimumUsers: 100,
      features: [
        'Everything in Ultimate for all students',
        'Bulk student account management',
        'LMS integration (Canvas, Moodle, Blackboard)',
        'Institutional analytics dashboard',
        'Content sharing across student cohorts',
        'Lecturer dashboard and tools',
        'AI-powered assignment generation',
        'Bulk study guide creation',
        'Custom branding and white-labeling',
        'Dedicated account manager',
        'Training and onboarding',
        'GDPR and FERPA compliance',
        '24/7 priority support',
        'Custom integrations'
      ]
    }
  },
  
  // Social Links
  social: {
    twitter: 'https://twitter.com/studysmith',
    instagram: 'https://instagram.com/studysmith',
    linkedin: 'https://linkedin.com/company/studysmith'
  },

  // Demo Configuration
  demo: {
    guideId: process.env.REACT_APP_DEMO_GUIDE_ID || null,
    // Utility functions
    isDemoConfigured() {
      return !!this.guideId;
    },
    isDemoRoute() {
      return window.location.pathname.includes('/demo');
    },
    shouldUseDatabaseDemo() {
      return this.isDemoConfigured() && this.isDemoRoute();
    }
  },
  
  // API Configuration
  api: {
    baseUrl: process.env.REACT_APP_API_URL || (
      process.env.NODE_ENV === 'production' 
        ? 'https://studysmith-api.fly.dev' 
        : 'http://localhost:5001'
    ),
    endpoints: {
      // Authentication endpoints
      auth: {
        signup: '/api/v1/auth/signup',
        signin: '/api/v1/auth/signin',
        signout: '/api/v1/auth/signout',
        refresh: '/api/v1/auth/refresh',
        user: '/api/v1/auth/user',
        resetPassword: '/api/v1/auth/reset-password',
        updateProfile: '/api/v1/auth/update-profile',
        oauth: '/api/v1/auth/oauth',
        health: '/api/v1/auth/health'
      },
      // Waiting list endpoints
      waitingList: '/api/v1/waitlist/join',
      // Dashboard endpoints (future)
      dashboard: '/api/dashboard'
    }
  },
  
  // University Information
  universities: [
    {
      name: 'University of Galway',
      logo: '/logos/uog.png', // Optional logo path
      shortName: 'UOG'
    },
    {
      name: 'Trinity College Dublin',
      logo: '/logos/tcd.png',
      shortName: 'Trinity'
    },
    {
      name: 'University College Dublin',
      logo: '/logos/ucd.png',
      shortName: 'UCD'
    },
    {
      name: 'University College Cork',
      logo: '/logos/ucc.png',
      shortName: 'UCC'
    },
    {
      name: 'Dublin City University',
      logo: '/logos/dcu.png',
      shortName: 'DCU'
    },
    {
      name: 'University of Limerick',
      logo: '/logos/ul.png',
      shortName: 'UL'
    },
    {
      name: 'Maynooth University',
      logo: '/logos/mu.png',
      shortName: 'Maynooth'
    },
    {
      name: 'Other',
      logo: null,
      shortName: 'Other'
    }
  ]
};

export default CONFIG; 
